import { useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import type { ChatMessage } from '@multimod/shared';
import { Ban, Link, MessageSquareX, UserX, MoreHorizontal } from 'lucide-react';
import {
  DialogShell,
  DialogFooter,
  DialogCancel,
  DialogDestructive,
  ReversibilityNote,
  PLATFORM_NAME,
  arrowNavHandler,
} from './_shell';

type Reason = 'spam' | 'hate' | 'self-promo' | 'outro';

interface ReasonOption {
  slug: Reason;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

// #111: slug persistido no audit log; label exibida na UI.
// #104: ícone Lucide por motivo + chip estilo prototype (rounded-sm + grid 2x2).
const REASONS: readonly ReasonOption[] = [
  { slug: 'spam', label: 'Spam / link suspeito', Icon: Link },
  { slug: 'hate', label: 'Hate speech', Icon: MessageSquareX },
  { slug: 'self-promo', label: 'Self-promo', Icon: UserX },
  { slug: 'outro', label: 'Outro', Icon: MoreHorizontal },
];

interface Props {
  open: boolean;
  msg: ChatMessage;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: Reason) => void;
}

export function ConfirmBanDialog({ open, msg, onOpenChange, onConfirm }: Props) {
  const [reason, setReason] = useState<Reason | null>(null);
  const platformName = PLATFORM_NAME[msg.platform];
  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={<Ban className="w-5 h-5" aria-hidden="true" />}
      iconTone="destructive"
      title={`Banir @${msg.author.displayName}?`}
      description={
        <>
          A ação será aplicada na <strong className="text-fg font-medium">{platformName}</strong>{' '}
          apenas. O usuário não vai conseguir mais enviar mensagens neste canal.
        </>
      }
      msg={msg}
      initialFocusSelector="button[aria-pressed]"
    >
      <div>
        <p className="text-xs text-fg-muted mb-2">Motivo (vai pro log):</p>
        {/* Toggle group via aria-pressed: padrão WAI-ARIA válido pra seleção
            única que não exige roving tabindex/arrow-keys do radiogroup.
            Arrows movem foco entre chips (sem auto-select; Enter/Space ativa). */}
        <div
          className="grid grid-cols-2 gap-1.5"
          role="group"
          aria-label="Motivo do ban"
          onKeyDown={arrowNavHandler}
        >
          {REASONS.map(({ slug, label, Icon }) => {
            const selected = reason === slug;
            return (
              <button
                key={slug}
                type="button"
                aria-pressed={selected}
                onClick={() => setReason(slug)}
                className={
                  'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm border text-xs font-medium transition-colors duration-fast ' +
                  (selected
                    ? 'border-destructive/50 bg-destructive/15 text-destructive'
                    : 'border-border bg-bg-input text-fg-muted hover:border-border-strong hover:text-fg')
                }
              >
                <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <ReversibilityNote>
        <strong className="text-fg font-medium">Reversível.</strong> Você pode revogar o ban no
        painel do {platformName} a qualquer momento.
      </ReversibilityNote>
      <DialogFooter
        hint={
          <>
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">Esc</kbd> cancela ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">⌘</kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">⏎</kbd> confirma
          </>
        }
      >
        <DialogCancel />
        {/* Texto curto: o nome já aparece no title + subject card. Repetir
            no botão causava quebra em 2 linhas em viewports menores. */}
        <DialogDestructive disabled={!reason} onClick={() => reason && onConfirm(reason)}>
          Banir usuário
        </DialogDestructive>
      </DialogFooter>
    </DialogShell>
  );
}
